/**
 * @file   src\mobile\components\createWantAd\AddTitle.js
 * @brief  This file is responsible for add title section in create want ad section
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import { handleTextBoxChange } from "../../../../actions/common";
import {
  getLocalStorage,
  getUserType,
  setBodyBackgroundWhite,
  setLocalStorage,
  trimString,
} from "../../../../helpers/common";
import { textValidations } from "../../../../helpers/validations";
import {
  BUY,
  ERROR_TEXT,
  IS_LAND,
  WANT_AD_KEY,
  WANT_AD_KEY_TEMP,
} from "../../../../constants/common";
import ProgressBar from "../../v2/ProgressBar";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import InfoIcon from "../../../components/infoIcn";
import Tooltip from "react-bootstrap/Tooltip";
import TextAreaMob from "../../TextAreaMob";
import TextBoxMob from "../../TextBoxMob";
import BuyerAgreement from "../../../../components/AgentWantAd/v2/BuyerAgreement";
import AddSyndication from "./AddSyndication";
import { useSelector } from "react-redux";
const AddTitle = (props) => {
  const {
    completedPercentage,
    handleFormValues,
    formValues,
    setUIKey,
    setCompletedPercentage,
    isEdit,
    isFromBuyerListingService,
    isAgent,
  } = props;
  const adTitle = _.get(formValues, "adTitle", "");
  const adTitleInSession = _.get(getLocalStorage(WANT_AD_KEY), "adTitle", "");
  const adDescription = _.get(formValues, "adDesc", "");
  const buyerAgreement = _.get(formValues, "buyersAgreementSigned", false);
  const syndicationFeed = _.get(formValues, "syndicationFeed", false);
  const syndicationPrivacy = _.get(formValues, "syndicationPrivacy", false);
  const adDescriptionInSession = _.get(
    getLocalStorage(WANT_AD_KEY),
    "adDesc",
    "",
  );
  const buyerAgreementInSession = _.get(
    getLocalStorage(WANT_AD_KEY),
    "buyersAgreementSigned",
    false,
  );
  const syndicationFeedInSession = _.get(
    getLocalStorage(WANT_AD_KEY),
    "syndicationFeed",
  );
  const syndicationPrivacyInSession = _.get(
    getLocalStorage(WANT_AD_KEY),
    "syndicationPrivacy",
  );
  //IS PREMIUM USER
  const [isPremiumUser, setIsPremiumUser] = useState(null);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const userType = getUserType();
  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
      } else {
        setIsPremiumUser(false);
      }
    }
  }, [userDetailsFromStore]);
  const [titleCharCount, setTitleCharCount] = useState(0);
  const [descCharCount, setDescCharCount] = useState(0);
  const [titleError, setTitleError] = useState(null);
  const [descError, setDescError] = useState(null);

  const [disableSave, setDisableSave] = useState(true);

  useEffect(() => {
    setTitleCharCount(adTitle && adTitle.length ? adTitle.length : 0);
    setDescCharCount(
      adDescription && adDescription.length ? adDescription.length : 0,
    );
    let isTitleValid = true;
    let isDescValid = true;
    if (!adTitle) {
      isTitleValid = false;
    }
    if (trimString(adTitle) !== "") {
      isTitleValid = textValidations(adTitle);
      if (!isTitleValid) {
        setTitleError(ERROR_TEXT);
      }
    }
    if (trimString(adDescription) !== "") {
      isDescValid = textValidations(adDescription);
      if (!isDescValid) {
        setDescError(ERROR_TEXT);
      }
    }
    const isCheckboxChanged =
      !!formValues?.buyersAgreementSigned != !!buyerAgreementInSession;
    const isSyndicationFeedChanged =
      syndicationFeed != syndicationFeedInSession;
    const isSyndicationPrivacyChanged =
      syndicationPrivacy != syndicationPrivacyInSession;
    if (isDescValid && isTitleValid) {
      if (
        isEdit &&
        adDescription === adDescriptionInSession &&
        adTitle === adTitleInSession &&
        !isCheckboxChanged &&
        !isSyndicationFeedChanged &&
        !isSyndicationPrivacyChanged
      ) {
        disableContinueButton();
      } else {
        setDisableSave(false);
      }
    } else {
      disableContinueButton();
    }
  }, [
    adTitle,
    adDescription,
    formValues?.buyersAgreementSigned, // Add checkbox state here
    titleError,
    descError,
    syndicationFeed,
    syndicationPrivacy,
  ]);

  const disableContinueButton = () => {
    const isDisabled = true;
    setDisableSave(isDisabled);
  };
  /**
   * To handle title change
   * @param {*} e
   */
  const handleTitleChange = (e) => {
    setTitleError(null);
    const value = capitalizeFirstString(handleTextBoxChange(e));
    setTitleCharCount(value.length);
    if (trimString(value) !== "") {
      handleFormValues({ adTitle: value });
    } else {
      handleFormValues({ adTitle: "" });
    }
  };
  /**
   * To capitalize first string of sentence
   * @param {*} str
   * @returns
   */
  const capitalizeFirstString = (str) => {
    return str && str.charAt(0).toUpperCase() + str.slice(1);
  };
  /**
   * To handle description change
   * @param {*} e
   */
  const handleDescriptionChange = (e) => {
    setDescError(null);
    const value = capitalizeFirstString(handleTextBoxChange(e));

    setDescCharCount(value.length);
    if (trimString(value) !== "") {
      handleFormValues({ adDesc: value });
    } else {
      handleFormValues({ adDesc: "" });
    }
  };
  useEffect(() => {
    setBodyBackgroundWhite();
  }, []);

  return (
    <>
      <div className="gitcha-main-wrapper pb-102 bg-white">
        <div className="content-wrapper content-wrapper-h-100-percent">
          <ProgressBar
            completedPercentage={completedPercentage}
            setUIKey={setUIKey}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
          <div className="inner-layout-content-new bg-white pl-16 pr-16 pb-0 h-100">
            <div className="content-btm-sec-title">
              <h6 className="">Description</h6>
              <h2 className="">Give your listing some personality</h2>
              <p className="fnt14 pt-3 ">
                Users who add a description to their listings are more likely to
                have higher quality introductions.
              </p>
            </div>

            <div className="mb-4 ">
              <div className="row">
                <div className="col-12 ">
                  <div className="mb-4 position-relative">
                    <div className="mob-fld-wrap">
                      <label className="form-label">
                        Title <span>*</span>
                      </label>

                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip placement="bottom">
                            "In accordance with the Federal Fair Housing Act, we
                            recommend not including personally identifiable
                            information, such as race, gender, sexual preference
                            or age. To learn more,{" "}
                            <a
                              href="https://www.hud.gov/program_offices/fair_housing_equal_opp/fair_housing_rights_and_obligations"
                              target="_blank"
                            >
                              read about the Fair Housing Act here.
                            </a>
                          </Tooltip>
                        }
                      >
                        <a href={void 0}>
                          <InfoIcon />
                        </a>
                      </OverlayTrigger>
                    </div>
                    <TextBoxMob
                      placeholder="Ex. Looking for dream home in city"
                      value={adTitle ? adTitle : ""}
                      errorMessage={titleError}
                      onChange={(e) => {
                        handleTitleChange(e);
                      }}
                      maxLength={50}
                    ></TextBoxMob>
                    <div className="text-end fnt10 pt-0 des-count-mob des-count-74">
                      {titleCharCount}/50
                    </div>
                  </div>
                </div>
                <div className="col-12 ">
                  <div className="mb-4 position-relative">
                    <div className="mob-fld-wrap">
                      <label className="form-label">Description</label>
                    </div>
                    <TextAreaMob
                      value={adDescription ? adDescription : ""}
                      errorMessage={descError}
                      maxLength={2000}
                      placeholder="Ex. My family and I have been on the hunt for our dream home in a walkable neighborhood..."
                      onChange={(e) => {
                        handleDescriptionChange(e);
                      }}
                    ></TextAreaMob>
                    <div className="text-end fnt10 pt-1 des-count-mob des-count-157">
                      {descCharCount}/2000
                    </div>
                  </div>
                </div>
                {userType == 2 || userType == 3 ? (
                  <>
                    {formValues.adType == BUY ? (
                      <>
                        <div className="col-12">
                          <h6>Buyer Agreement</h6>
                          <BuyerAgreement
                            isChecked={_.get(
                              formValues,
                              "buyersAgreementSigned",
                              false,
                            )}
                            handleFormValues={handleFormValues}
                          />
                        </div>
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {userType == 2 || userType == 3 ? (
          <AddSyndication
            handleFormValues={handleFormValues}
            wantAdData={formValues}
            isDisabled={isPremiumUser ? false : true}
          />
        ) : null}
        <div className="inner-layout-footer-new">
          <div className="inner-btm-fixed-nav">
            <div className="btn-wrap text-center pb-40 pt-0">
              <button
                type="button"
                className="btn btn-primary w-100 mb-0"
                onClick={() => {
                  if (!formValues?.syndicationFeed) {
                    handleFormValues({ syndicationFeed: "public" });
                  }
                  if (!formValues?.syndicationPrivacy) {
                    handleFormValues({ syndicationPrivacy: "hide" });
                  }
                  setCompletedPercentage(100);
                  setTimeout(() => {
                    setLocalStorage(
                      WANT_AD_KEY,
                      getLocalStorage(WANT_AD_KEY_TEMP),
                    );
                    if (!isEdit) {
                      if (
                        isAgent &&
                        (_.get(formValues, "adType", "") == BUY ||
                          _.get(formValues, "prprtyType", "") == IS_LAND)
                      ) {
                        setUIKey("compensation");
                      } else {
                        setUIKey("readyToPost");
                      }
                    } else {
                      setUIKey(null);
                    }
                  }, 500);
                }}
                disabled={disableSave}
              >
                {isEdit ? "Update" : isAgent ? "Next" : "Review & Post"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTitle;
